import React, { useState, useCallback } from 'react';
import { StyleSheet, ScrollView } from 'react-native';
import {
  DNABox,
  DNAText,
  DNAButton,
  Toggle,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { ComplianceRuleStatus } from '../../types/enums';

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors['color-text-white'],
  },
  ruleItem: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderColor: colors['color-gray-80'],
    marginBottom: 12,
    borderWidth: 1,
    borderRadius: 8,
  },
  ruleItemDisabled: {
    backgroundColor: colors['color-ui2-neutral-50'],
  },
  ruleText: {
    color: colors['color-text-primary'],
    lineHeight: 20,
    marginBottom: 4,
  },
  ruleTextDisabled: {
    color: colors['color-text-secondary'],
    opacity: 0.6,
  },
  ruleControlsDisabled: {
    opacity: 0.5,
  },
  badge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  badgePassed: {
    backgroundColor: colors['color-success-100'],
  },
  badgeFailed: {
    backgroundColor: colors['color-danger-100'],
  },
  badgeNotApplicable: {
    backgroundColor: colors['color-gray-100'],
  },
  badgeText: {
    textTransform: 'uppercase',
  },
  badgeTextPassed: {
    color: colors['color-success-500'],
  },
  badgeTextFailed: {
    color: colors['color-danger-500'],
  },
  badgeTextNotApplicable: {
    color: colors['color-gray-500'],
  },
  explanationContainer: {
    marginTop: 4,
    paddingTop: 8,
  },
  explanationText: {
    color: colors['color-text-secondary'],
    lineHeight: 18,
  },
  viewMoreButton: {
    marginTop: 4,
    marginBottom: 4,
  },
  emptyStateContainer: {
    padding: 40,
  },
  emptyText: {
    color: colors['color-text-secondary'],
  },
});

export interface ComplianceRuleStatusData {
  id: string;
  text: string;
  status: ComplianceRuleStatus;
  explanation?: string;
  enabled: boolean;
}

interface ComplianceRuleStatusListProps {
  rules: ComplianceRuleStatusData[];
  onToggleRule?: (ruleId: string, enabled: boolean) => void;
  showDetails?: boolean;
}

const STATUS_CONFIG: Record<ComplianceRuleStatus, { label: string; badgeStyle: any; textStyle: any }> = {
  [ComplianceRuleStatus.PASSED]: {
    label: 'Passed',
    badgeStyle: styles.badgePassed,
    textStyle: styles.badgeTextPassed,
  },
  [ComplianceRuleStatus.FAILED]: {
    label: 'Failed',
    badgeStyle: styles.badgeFailed,
    textStyle: styles.badgeTextFailed,
  },
  [ComplianceRuleStatus.NOT_APPLICABLE]: {
    label: 'Not Applicable',
    badgeStyle: styles.badgeNotApplicable,
    textStyle: styles.badgeTextNotApplicable,
  },
};

const EXPLANATION_MAX_LENGTH = 150;

const useExplanationTruncation = () => {
  const getPreview = useCallback((explanation: string) => {
    if (explanation.length <= EXPLANATION_MAX_LENGTH) {
      return explanation;
    }
    return explanation.substring(0, EXPLANATION_MAX_LENGTH) + '...';
  }, []);

  const shouldShowTruncated = useCallback((explanation: string) => {
    return explanation.length > EXPLANATION_MAX_LENGTH;
  }, []);

  return { getPreview, shouldShowTruncated };
};

interface ComplianceRuleStatusItemProps {
  rule: ComplianceRuleStatusData;
  isLast: boolean;
  onToggleRule?: (ruleId: string, enabled: boolean) => void;
  showDetails?: boolean;
}

const ComplianceRuleStatusItem: React.FC<ComplianceRuleStatusItemProps> = ({
  rule,
  onToggleRule,
  showDetails = true,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { getPreview, shouldShowTruncated } = useExplanationTruncation();
  const statusConfig = STATUS_CONFIG[rule.status];
  const hasExplanation = Boolean(rule.explanation);

  const handleToggle = () => {
    if (onToggleRule) {
      onToggleRule(rule.id, !rule.enabled);
    }
  };

  return (
    <DNABox
      style={[styles.ruleItem, !rule.enabled && styles.ruleItemDisabled]}
      testID={`compliance-rule-status-${rule.id}`}
      spacing="sm"
      appearance="col"
    >
      <DNAText p2 style={[styles.ruleText, !rule.enabled && styles.ruleTextDisabled]}>
        {rule.text}
      </DNAText>

      {showDetails && hasExplanation && (
        <DNABox style={styles.explanationContainer}>
          <DNAText c1 style={styles.explanationText}>
            {expanded
              ? rule.explanation
              : getPreview(rule.explanation!)
            }
          </DNAText>
        </DNABox>
      )}

      {showDetails && hasExplanation && shouldShowTruncated(rule.explanation!) && (
        <DNABox alignX="start">
          <DNAButton
            appearance="outline"
            status="tertiary"
            size="sm"
            onPress={() => setExpanded(!expanded)}
            style={styles.viewMoreButton}
            testID={`view-more-${rule.id}`}
            disabled={!rule.enabled}
          >
            {expanded ? 'View less' : 'View more'}
          </DNAButton>
        </DNABox>
      )}

      {showDetails && (
        <DNABox
          appearance="row"
          spacing="between"
          alignY="center"
          style={!rule.enabled && styles.ruleControlsDisabled}
        >
          <DNABox alignY="start">
            <DNABox style={[styles.badge, statusConfig.badgeStyle]}>
              <DNAText c2 bold style={[styles.badgeText, statusConfig.textStyle]}>
                {statusConfig.label}
              </DNAText>
            </DNABox>
          </DNABox>

          {onToggleRule && (
            <Toggle.Kitten
              checked={rule.enabled}
              onChange={handleToggle}
              testID={`compliance-rule-toggle-${rule.id}`}
              status="success"
              size="sm"
            />
          )}
        </DNABox>
      )}
    </DNABox>
  );
};

const ComplianceRuleStatusList: React.FC<ComplianceRuleStatusListProps> = ({
  rules,
  onToggleRule,
  showDetails = true,
}) => {
  if (!rules || rules.length === 0) {
    return (
      <DNABox appearance="col" alignX="center" style={styles.emptyStateContainer}>
        <DNAText p2 style={styles.emptyText}>
          No compliance rules to display
        </DNAText>
      </DNABox>
    );
  }

  return (
    <DNABox style={styles.container} appearance="col" fill>
      <ScrollView
        showsVerticalScrollIndicator={true}
        bounces={false}
      >
        {rules.map((rule, index) => (
          <ComplianceRuleStatusItem
            key={rule.id}
            rule={rule}
            isLast={index === rules.length - 1}
            onToggleRule={onToggleRule}
            showDetails={showDetails}
          />
        ))}
      </ScrollView>
    </DNABox>
  );
};

export default ComplianceRuleStatusList;
